/**
 * Coming back after a health stop (Food Code 2-201.13), the person in charge's recorded decision, and the honest words
 * about who has been told (fix/s2-illness-privacy-truth, 2026-09-29).
 *
 * Every rule here is the house table, nothing from memory:
 *   docs/source/parish-food-safety-protocol.md "Returning to work (Food Code 2-201.13)" (the same rules as its own table in
 *   docs/source/coffee-shop-food-safety-protocol.md, "Returning to work"), quoted:
 *     | Vomiting or diarrhea, no diagnosis | 24 hours symptom-free, or a doctor's note that the cause isn't infectious |
 *     | Hepatitis A or jaundice | County health department approval, plus one of: jaundice began more than 7 days ago;
 *       other symptoms began more than 14 days ago; or a doctor's note stating the person is free of hepatitis A |
 *     | Typhoid (Salmonella Typhi) | County health department approval and a written doctor's note ... |
 *     | Norovirus | County health department approval (2-201.13(D)), plus one of: a doctor's note ...; 48 hours after
 *       symptoms end; or 48 hours after diagnosis with no symptoms |
 *     | Shigella | County health department approval (2-201.13(E)), plus one of: two negative stool cultures 24+ hours
 *       apart ...; 7 days after symptoms end; or 7 days after diagnosis with no symptoms |
 *     | STEC | County health department approval (2-201.13(F)), plus one of: ... 7 days after symptoms end; or 7 days ... |
 *     | Nontyphoidal Salmonella | County health department approval (2-201.13(G)), plus one of: ... 30 days after
 *       symptoms end; or 30 days after diagnosis with no symptoms |
 *     | Sore throat with fever | Doctor's note: 24+ hours of antibiotics, a negative throat culture, or cleared by the doctor |
 *     | Pus-filled lesion | Covered as required (bandage plus glove on hands) |
 *   and the diagnosed row of the parish health table: "Diagnosed with Norovirus, Hepatitis A, Shigella, Shiga
 *   toxin-producing E. coli, Salmonella Typhi or nontyphoidal Salmonella | Exclude; the Coordinator reports to the county
 *   health department", with Food Code 8-501.10(C) (Utah amendment: illness reporting under R386-702 and R386-703).
 *
 * The check-in asks "diagnosed?" for all six illnesses, then "symptoms now?" (fix/checkin-diagnosed-split). Every one of
 * the six needs the regulatory authority's approval to come back, per the FDA Food Code 2022 text (2-201.13(B)-(G):
 * "if the PERSON IN CHARGE obtains APPROVAL from the REGULATORY AUTHORITY"). The house return table said so only for
 * hepatitis A and typhoid until 2026-09-29; the owner decided the law wins, and both house tables now require it for all
 * six. So the line is the true one for all six: call the local health department before food work again. The
 * per-illness rows are shown under it, word for word; the restrict rows list only the four a restriction can be for.
 *
 * The Utah contact: the repo sources name the regulator ("In Utah the regulatory authority is the local (county or
 * district) health department", docs/source/research/business-setup-utah.md) but hold no phone number for it, and
 * several Utah counties share a district department, so the app names "your local health department" and the
 * location's county, and never prints a number it doesn't have (ASSUMED gap, flagged in the fix report).
 */
import { CHECKIN_ACTIONS, stopsFoodWork, type CheckinAction } from "./checkin.ts";
import { RETURNTOWORK_UT } from "./states/ut/returntowork.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { cite, wordsFor } from "./states/words.ts";
import type { HandoffTarget } from "./handoff.ts";
import { namesLine } from "./handoff.ts";
import type { Id, IsoDate, IsoDateTime } from "./model.ts";

/** What the person in charge can record as met before someone comes back (the house table's "May return when"). */
export type ReturnConditionId = "symptom_free_24h" | "doctor_not_infectious" | "doctor_throat" | "covered" | "health_dept_ok";

/**
 * The labels the person in charge sees and the record keeps: the rule met, never the symptom (T4). The worker's own
 * screen gives the specific rule (RETURN_RULES.worker).
 */
export const RETURN_CONDITIONS: Record<ReturnConditionId, { label: string }> = {
  symptom_free_24h: { label: "24 hours with no symptoms" },
  doctor_not_infectious: { label: "a doctor's note: it isn't infectious" },
  doctor_throat: { label: "a doctor's note that clears them" },
  covered: { label: "covered: a bandage and a glove" },
  health_dept_ok: { label: "the health department said OK" },
};

export interface ReturnRule {
  /**
   * For the worker: when they may come back, in plain words. Never names the symptom: the result can sit on a shared
   * tablet for a moment, and the screen never repeats an answer back (checkin.ts, UX_SCOPE).
   */
  worker: string;
  /** For the person in charge: the same rule without the symptom (T4), as the decision it asks for. */
  personInCharge: string;
  /** The return needs the local health department (a diagnosis or jaundice: 2-201.13, 8-501.10(C)). */
  healthDept: boolean;
  /** The conditions the person in charge can record as met ("Cleared to return: ..."). */
  conditions: ReturnConditionId[];
  citation: string;
  /** The table's own rows, word for word in plain English (shown under the rule for a diagnosis). */
  rows?: string[];
}

/**
 * The per-illness rows of the house table, for someone a doctor diagnosed (the app doesn't ask which of the six unless it
 * changes the action). Each keeps the table's "or ... after diagnosis with no symptoms" where the table has it.
 */
const NORO_ROW = "Norovirus: a doctor's clearance, or 48 hours after symptoms end, or 48 hours after the diagnosis if you had no symptoms.";
const SHIGELLA_STEC_ROW = "Shigella or E. coli (STEC): two negative stool tests, or 7 days after symptoms end, or 7 days after the diagnosis if you had no symptoms.";
const SALMONELLA_ROW = "Salmonella (not typhoid): two negative stool tests, or 30 days after symptoms end, or 30 days after the diagnosis if you had no symptoms.";
export const DIAGNOSED_RETURN_ROWS = [
  "Hepatitis A, or yellow skin or eyes: the health department's OK, plus a doctor's note or enough days passed.",
  "Typhoid: the health department's OK and a written doctor's note.",
  NORO_ROW, SHIGELLA_STEC_ROW, SALMONELLA_ROW,
] as const;
/** A restriction for a diagnosis is only ever norovirus, Shigella, STEC or nontyphoidal Salmonella (checkin.ts). */
export const RESTRICTED_RETURN_ROWS = [NORO_ROW, SHIGELLA_STEC_ROW, SALMONELLA_ROW] as const;

/**
 * When someone stopped by the check-in may come back, per action (undefined: nothing to come back from). The most severe
 * answer decides the action (checkin.ts), and its row decides the return.
 */
const WORDS: Record<UsState, StateWords<typeof RETURNTOWORK_UT>> = perStateTable("RETURNTOWORK", RETURNTOWORK_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

/** The return rules for a state (only the health-department citations differ; Utah's is RETURN_RULES). */
export function returnRulesFor(state?: UsState): Record<CheckinAction, ReturnRule | undefined> {
  const w = words(state);
  return {
  ok: undefined,
  // The house table: "Exposure to a Big 6 illness, no symptoms | No Food Code requirement; parish policy restricts from
  // ready-to-eat food". Not a Food Code exclusion, so no return rule: tomorrow's check-in asks again.
  caution: {
    worker: "This is the safer choice, not a Food Code rule. Tomorrow's check-in asks again.",
    personInCharge: "No Food Code return rule for this. Tomorrow's check-in asks again.",
    healthDept: false, conditions: [], citation: "Food Code 2-201.12(J); house table: exposure with no symptoms",
  },
  cover: {
    worker: "As soon as it's covered: a bandage, and a glove on that hand.",
    personInCharge: "Back to food work once it's covered: a bandage and a glove.",
    healthDept: false, conditions: ["covered"], citation: "Food Code 2-201.13; 2-401.13",
  },
  restrict: {
    worker: "You can go back to food work with a doctor's note: 24 hours of antibiotics, a negative test, or cleared by the doctor.",
    personInCharge: "Back to food work with a doctor's note that clears them.",
    healthDept: false, conditions: ["doctor_throat"], citation: "Food Code 2-201.13",
  },
  // A diagnosis with no symptoms (2-201.12(D)(2), (E)(2), (F)(2), (G)): other work now, food work only with the
  // regulatory authority's approval plus the illness's own condition (2-201.13(D)-(G)).
  restrict_county: {
    worker: "Call the health department before you do food work again. They tell you when.",
    personInCharge: "Call the health department. Back to food work only when they say OK.",
    healthDept: true, conditions: ["health_dept_ok"], citation: cite("Food Code 2-201.13(D)-(G)", w.illnessAmendCite),
    rows: [...RESTRICTED_RETURN_ROWS],
  },
  exclude: {
    worker: "You can come back 24 hours after your symptoms stop. A doctor's note that it isn't infectious also works.",
    personInCharge: "Back 24 hours after symptoms stop, or with a doctor's note that it isn't infectious.",
    healthDept: false, conditions: ["symptom_free_24h", "doctor_not_infectious"], citation: "Food Code 2-201.13",
  },
  exclude_county: {
    worker: "Call the health department before you come back. They tell you when.",
    personInCharge: "Call the health department. Back only when they say OK.",
    healthDept: true, conditions: ["health_dept_ok"], citation: cite("Food Code 2-201.13(B)-(G)", w.illnessAmendCite),
    rows: [...DIAGNOSED_RETURN_ROWS],
  },
  };
}

export const RETURN_RULES: Record<CheckinAction, ReturnRule | undefined> = returnRulesFor("UT");

export const HEALTH_DEPT_COUNTY_LINE = "Your local health department is the one for {county} County.";
export const HEALTH_DEPT_ANY_LINE = "Your local health department is the one for your county.";
/** Who to call, from the location's county ("Salt Lake"), never a number the sources don't hold. An English line and its slot. */
export function healthDeptLine(county?: string): { line: string; vars?: { county: string } } {
  const c = county?.trim().replace(/\s+county$/i, "");
  return c ? { line: HEALTH_DEPT_COUNTY_LINE, vars: { county: c } } : { line: HEALTH_DEPT_ANY_LINE };
}

/* ─────────────── the person in charge's decision (recorded, audited, private) ─────────────── */

export type HealthDecisionKind = "exclude" | "restrict" | "cleared";

/** One decision by the person in charge about one worker. Stored as an audit event; never carries a symptom. */
export interface HealthDecision {
  employeeId: Id;
  locationId: Id;
  decision: HealthDecisionKind;
  /** exclude: the first day they may be back (a date the person in charge chose). None: until the health department says OK. */
  until?: IsoDate;
  /** cleared: which return condition was met. */
  condition?: ReturnConditionId;
  at: IsoDateTime;
  /** User id of the person who decided. */
  by: Id;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** "Wed, Oct 1" (the date alone, no time zone involved). */
export function decisionDayLabel(d: IsoDate): string {
  const t = new Date(`${d}T12:00:00Z`);
  return `${WEEKDAYS[t.getUTCDay()]}, ${MONTHS[t.getUTCMonth()]} ${t.getUTCDate()}`;
}

/** "Excluded until Wed, Oct 1" · "Excluded until the health department says OK" · "Restricted: no food work" · "Cleared to return: ..." */
export function healthDecisionLine(d: Pick<HealthDecision, "decision" | "until" | "condition">): string {
  if (d.decision === "exclude") return d.until ? `Excluded until ${decisionDayLabel(d.until)}` : "Excluded until the health department says OK";
  if (d.decision === "restrict") return "Restricted: no food work";
  return `Cleared to return: ${d.condition ? RETURN_CONDITIONS[d.condition].label : "the return rule is met"}`;
}

/** Does this decision still keep them off food work on `date`? An exclusion ends on its date; a restriction when cleared. */
export function decisionInEffect(d: Pick<HealthDecision, "decision" | "until">, date: IsoDate): boolean {
  if (d.decision === "exclude") return !d.until || date < d.until;
  return d.decision === "restrict";
}

/** The newest decision about this person (decisions from newest or oldest, any order). */
export function latestDecision<T extends Pick<HealthDecision, "employeeId" | "at">>(all: readonly T[] | undefined, employeeId: Id): T | undefined {
  return (all ?? []).filter((d) => d.employeeId === employeeId).reduce<T | undefined>((m, d) => (!m || d.at > m.at ? d : m), undefined);
}

/**
 * The Food Code action behind a person-in-charge gate line (shiftGate writes `${personInCharge} (${citation})`), so a
 * screen that has only the gate can offer the right return choices. Undefined for any other line.
 */
export function actionFromGateDetail(detail: string | undefined): CheckinAction | undefined {
  if (!detail) return undefined;
  return (Object.keys(CHECKIN_ACTIONS) as CheckinAction[]).find((a) => a !== "ok" && detail.startsWith(`${CHECKIN_ACTIONS[a].personInCharge} (`));
}

/** shiftGate's title for a decision in effect (worker and manager screens match on it). */
export const HEALTH_DECISION_BLOCK_TITLE = "no food work: the person in charge decided";

/**
 * Checks a decision before it's recorded. `action` is the Food Code action the person's latest check-in gave, if any.
 * A diagnosis or jaundice (exclude_county, restrict_county) comes back only with the health department's OK
 * (2-201.13(B)-(G)): no end date and no other condition. A restriction for a diagnosis may be made an exclusion (the
 * stricter choice), but never one with a date, since a dated exclusion would end without that OK.
 */
const needsHealthDept = (a?: CheckinAction) => !!a && RETURN_RULES[a]?.healthDept === true;
export function checkDecision(d: Pick<HealthDecision, "decision" | "until" | "condition">, today: IsoDate, action?: CheckinAction): { ok: true } | { ok: false; error: string } {
  if (d.decision === "exclude") {
    if (d.condition) return { ok: false, error: "An exclusion has no return condition yet. Record it when they're cleared." };
    if (d.until) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d.until) || d.until <= today) return { ok: false, error: "Pick a day after today for them to come back." };
      const max = new Date(Date.parse(`${today}T12:00:00Z`) + 60 * 86_400_000).toISOString().slice(0, 10);
      if (d.until > max) return { ok: false, error: "Pick a day within the next 60 days." };
      if (needsHealthDept(action)) return { ok: false, error: "Only the health department can set the day they come back. Leave the date off." };
    }
    return { ok: true };
  }
  if (d.decision === "restrict") return d.until || d.condition ? { ok: false, error: "A restriction lasts until you clear them." } : { ok: true };
  if (!d.condition) return { ok: false, error: "Say which return rule was met." };
  if (d.until) return { ok: false, error: "Clearing someone has no end date." };
  if (action && !RETURN_RULES[action]?.conditions.includes(d.condition)) {
    return { ok: false, error: needsHealthDept(action) ? "This needs the health department's OK first (Food Code 2-201.13)." : "That rule isn't the one for today's check-in." };
  }
  return { ok: true };
}

/** The choices the person in charge sees for someone stopped with `action` (unknown: a sick report with no check-in yet). */
export function decisionChoices(action: CheckinAction | undefined, today: IsoDate): { excludeDays: IsoDate[]; excludeUntilHealthDept: boolean; restrict: boolean; conditions: ReturnConditionId[] } {
  const next = (n: number) => new Date(Date.parse(`${today}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
  const county = needsHealthDept(action);
  const conditions = action ? (RETURN_RULES[action]?.conditions ?? []) : (["symptom_free_24h", "doctor_not_infectious", "doctor_throat", "covered", "health_dept_ok"] as ReturnConditionId[]);
  // restrict_county: "Restricted" is the Food Code's own action; exclude-until-OK stays as the stricter choice.
  return { excludeDays: county ? [] : [next(1), next(2), next(3)], excludeUntilHealthDept: true, restrict: !county || action === "restrict_county", conditions };
}

/* ─────────────── who has been told (truth: never "your manager knows" before the server has it) ─────────────── */

/** After a stop: the report is queued on this device, not sent. */
export const TOLD_QUEUED_LINE = "Saved on this phone. It will send when you're online. Tell your manager in person now.";
/**
 * A solo owner, a food truck, a lone shift lead: they are the person in charge, so nobody else is told. The house source:
 * "If you are sick and work alone, close for the day"; "As a one-person shop, 'restrict' in practice usually means
 * closing" (docs/source/coffee-shop-food-safety-protocol.md:739, :757).
 */
export const TOLD_SELF_LINE = "You're the person in charge. Get someone else on food work today, or close food service for the day.";
export const TOLD_NOBODY_LINE = "Nobody is set as in charge here yet. Tell your manager in person now.";

export const TOLD_SENT_TO_LINE = "Sent to {names}. They see what to do today, never your answers.";

/**
 * The line under a stop result (restrict, restrict_county, exclude, exclude_county), as an English line and its slots (screens translate
 * it with checkinText). `sent`: the server confirmed it has the check-in. Undefined for an action that keeps them working
 * (ok, caution) or only needs a bandage (cover).
 */
export function checkinToldLine(o: { sent: boolean; action: CheckinAction; target?: Pick<HandoffTarget, "via" | "to"> }): { line: string; vars?: { names: string } } | undefined {
  if (!stopsFoodWork(o.action)) return undefined;
  if (!o.sent) return { line: TOLD_QUEUED_LINE };
  const t = o.target;
  if (!t) return undefined; // sent, but whose list isn't known (an older server, or opened again later): say nothing untrue
  if (t.via === "self") return { line: TOLD_SELF_LINE };
  if (!t.to.length) return { line: TOLD_NOBODY_LINE };
  return { line: TOLD_SENT_TO_LINE, vars: { names: namesLine((t.via === "manager" ? t.to.slice(0, 1) : t.to).map((p) => p.name)) } };
}

/** Every worker-facing line above (the Spanish table covers each one: core checkinEs.ts). */
export function returnLinesEn(): string[] {
  const rules = Object.values(RETURN_RULES).filter((r): r is ReturnRule => !!r);
  return [...new Set([...rules.map((r) => r.worker), ...DIAGNOSED_RETURN_ROWS, TOLD_QUEUED_LINE, TOLD_SELF_LINE, TOLD_NOBODY_LINE, TOLD_SENT_TO_LINE, HEALTH_DEPT_COUNTY_LINE, HEALTH_DEPT_ANY_LINE])];
}

/** The notice to the person in charge when a check-in stops food work (the action and its return rule, never a symptom). */
export function checkinNoticeText(o: { fromName: string; action: CheckinAction }): { title: string; body: string } {
  const who = o.fromName.split(/\s+/)[0] || "Someone";
  const a = CHECKIN_ACTIONS[o.action];
  const r = RETURN_RULES[o.action];
  return { title: `${who}: no food work today`, body: `${a.personInCharge}${r ? ` ${r.personInCharge}` : ""} (${r?.citation ?? a.citation})` };
}

/**
 * The notice when someone who was stopped answers again and is now OK (or checks in OK after a sick report). The worker
 * isn't told anything is suspected; the person in charge is asked to check before food work (Food Code 2-201.12).
 */
export function changedAnswerNoticeText(fromName: string): { title: string; body: string } {
  const who = fromName.split(/\s+/)[0] || "Someone";
  return { title: `${who} changed their check-in answer`, body: "Check with them before food work (Food Code 2-201.12, 2-201.13)." };
}
